/**
 * UpcomingReviews - TASK-378
 * Shows upcoming KYC review dates by risk level.
 * Low=3yr, Medium=2yr, High=1yr, Critical=6mo.
 * Color-coded countdown display.
 */

import React from 'react';
import { Calendar, Clock, AlertTriangle } from 'lucide-react';
import { useRiskRating } from '../../../hooks/useCompliance';
import { formatDate } from '../../../utils/formatters';

type RiskLevel = 'low' | 'medium' | 'high' | 'critical';

interface ReviewSchedule {
  label: string;
  riskLevel: RiskLevel;
  intervalMonths: number;
  dueDate: Date;
}

const RISK_INTERVALS: Record<RiskLevel, number> = {
  low: 36,
  medium: 24,
  high: 12,
  critical: 6,
};

const RISK_COLORS: Record<RiskLevel, { bg: string; text: string; border: string; badge: string }> = {
  low: {
    bg: 'bg-green-50',
    text: 'text-green-700',
    border: 'border-green-200',
    badge: 'bg-green-100 text-green-700',
  },
  medium: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
    badge: 'bg-amber-100 text-amber-700',
  },
  high: {
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    border: 'border-orange-200',
    badge: 'bg-orange-100 text-orange-700',
  },
  critical: {
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
    badge: 'bg-red-100 text-red-700',
  },
};

function addMonths(date: Date, months: number): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
}

function getDaysUntil(target: Date): number {
  const now = new Date();
  const diffMs = target.getTime() - now.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

function formatCountdown(daysUntil: number): string {
  if (daysUntil < 0) return `${Math.abs(daysUntil)}d overdue`;
  if (daysUntil === 0) return 'Due today';
  if (daysUntil < 30) return `${daysUntil}d`;
  if (daysUntil < 365) return `${Math.floor(daysUntil / 30)}mo`;
  return `${(daysUntil / 365).toFixed(1)}yr`;
}

function getCountdownStatus(daysUntil: number): 'overdue' | 'urgent' | 'upcoming' | 'on_track' {
  if (daysUntil < 0) return 'overdue';
  if (daysUntil <= 30) return 'urgent';
  if (daysUntil <= 90) return 'upcoming';
  return 'on_track';
}

const COUNTDOWN_COLORS: Record<string, string> = {
  overdue: 'text-red-600 font-bold',
  urgent: 'text-amber-600 font-semibold',
  upcoming: 'text-blue-600',
  on_track: 'text-green-600',
};

function buildReviewSchedules(_currentRiskLevel: RiskLevel): ReviewSchedule[] {
  const now = new Date();

  return (Object.entries(RISK_INTERVALS) as [RiskLevel, number][]).map(([level, months]) => ({
    label: `${level.charAt(0).toUpperCase() + level.slice(1)} Risk Review`,
    riskLevel: level,
    intervalMonths: months,
    dueDate: addMonths(now, months),
  }));
}

export const UpcomingReviews: React.FC = () => {
  const riskRating = useRiskRating();
  const currentRisk = (riskRating?.overall as RiskLevel) || 'low';
  const schedules = buildReviewSchedules(currentRisk);

  // Sort by due date (soonest first)
  const sorted = [...schedules].sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime());

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
        <Calendar className="w-5 h-5 text-gray-500" />
        <h3 className="font-medium text-gray-900">Upcoming Reviews</h3>
      </div>

      {/* Review list */}
      <div className="divide-y divide-gray-100">
        {sorted.map((schedule) => {
          const daysUntil = getDaysUntil(schedule.dueDate);
          const countdownStatus = getCountdownStatus(daysUntil);
          const colors = RISK_COLORS[schedule.riskLevel];
          const isActive = schedule.riskLevel === currentRisk;

          return (
            <div
              key={schedule.riskLevel}
              className={`px-4 py-3 flex items-center justify-between ${
                isActive ? colors.bg : ''
              }`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900">{schedule.label}</p>
                  {isActive && (
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${colors.badge}`}>
                      Current
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  Due: {formatDate(schedule.dueDate)} (every {schedule.intervalMonths}mo)
                </p>
              </div>

              <div className="flex items-center gap-2 shrink-0 ml-3">
                {countdownStatus === 'overdue' && (
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                )}
                <div className="text-right">
                  <p className={`text-sm ${COUNTDOWN_COLORS[countdownStatus]}`}>
                    {formatCountdown(daysUntil)}
                  </p>
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <Clock className="w-3 h-3" />
                    Remaining
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UpcomingReviews;
